import React, { useCallback, useEffect, useState } from 'react';
import styles from './BaseAddress.module.scss';
import { CountryDropdown, TextInput } from '../../../../../commons/hoc';
import { statesWithId } from '../../../../body/support/forms/shared/FormsData';
import { registerInput } from '../../sections/address/registers';
import {
  addressFormater,
  cleanNameField,
  phoneNumberLengthValidation,
  processCityField,
  processNameField,
} from '../../../../../commons/formatters';
import { validationTypes } from '../../../../../commons/validations';
import {
  getValidationMessage,
  isEmptyObject,
} from '../../../../../commons/utils';
import {
  normalizeInput,
  validateCode,
  validateNewZipCode,
} from '../../../../body/checkout/sections/checkoutUtils';
import {
  googleMapsSearchOptions,
  getValidIndexValue,
  googleMapsValueSplit,
  googleMapsRequestOptions,
  googleMapsSearchOptionsAll,
  googleMapsSearchOptionsShipping,
} from '../../../../../commons/utils/utils';
import PlacesAutocomplete from 'react-places-autocomplete';
import { codeToStateMatrix } from '../../../../../utils/Utils';
import { useAppSelector } from '../../../../../src/app/hooks';
import { getAppState } from '../../../../../src/features/app/appState';
import { ZIP_CODE_REGEX } from '../../../../../commons/Regex';
import { postUiLogs } from '../../../../../src/features/user/userApi';

const FIRST_NAME = 'firstName';
const LAST_NAME = 'lastName';
const ADDRESS1 = 'address1';
const ADDRESS2 = 'address2';
const CITY = 'city';
const STATE = 'state';
const ZIP = 'zip';
const PHONE = 'phone';
const COUNTRY = 'countryId';

const countryList = [
  {
    name: 'United States',
    value: 'US',
  },
  {
    name: 'Canada',
    value: 'CA',
  },
];

const BaseAddress = ({
  id = '',
  formPrefix = '',
  isSupportPage = false,
  rdxUpdateAddressCB = () => {},
  hookFormCB = {},
  data,
  lockNextSections = () => {},
  addressType = 'shipping',
}) => {
  const formKeyMap = {
    [FIRST_NAME]: `${formPrefix + FIRST_NAME}`,
    [LAST_NAME]: `${formPrefix + LAST_NAME}`,
    [ADDRESS1]: `${formPrefix + ADDRESS1}`,
    [ADDRESS2]: `${formPrefix + ADDRESS2}`,
    [CITY]: `${formPrefix + CITY}`,
    [STATE]: `${formPrefix + STATE}`,
    [ZIP]: `${formPrefix + ZIP}`,
    [PHONE]: `${formPrefix + PHONE}`,
    [COUNTRY]: `${formPrefix + COUNTRY}`,
  };

  const { register, formState, clearErrors, trigger, setValue } = hookFormCB;

  const { errors } = formState;

  const [formData, setFormData] = useState({
    [FIRST_NAME]: '',
    [LAST_NAME]: '',
    [ADDRESS1]: '',
    [ADDRESS2]: '',
    [CITY]: '',
    [STATE]: '',
    [ZIP]: '',
    [PHONE]: '',
    [COUNTRY]: 'US',
  });
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [handleSuggestion, setHandleSuggestion] = useState(true);
  const appState = useAppSelector(getAppState);

  useEffect(() => {
    // Check if Google Maps API is loaded
    if (window?.google && window?.google?.maps.places) {
      setIsGoogleLoaded(true);
    } else {
      setIsGoogleLoaded(false);
    }
  }, []);

  const postUiValidationError = useCallback(() => {
    if (Object.keys(errors).length != 0) {
      postUiLogs({
        // errors: errors,
        formData: formData,
        addressType: addressType,
        message: 'Address validation Error',
      });
    }
  }, [errors]);

  const updateRedux = () => {
    postUiValidationError();
    rdxUpdateAddressCB(formData);
  };

  const updateReduxOnChange = (data) => {
    rdxUpdateAddressCB({ ...formData, ...data });
  };

  const handleInputOnChange = (key, value) => {
    setFormData((old) => ({ ...old, [key]: value }));
    clearErrors(`${formPrefix + key}`);
  };

  const onInputBlur = () => {
    if (Object.keys(errors).length === 0) {
      // releaseLockedSections('shipping');
    }
  };

  const handlePhonenumberFocus = (e, type) => {
    let result = e.target.value.replace(/\D/g, '');
    handleInputOnChange(PHONE, result);
    setValue(`${formPrefix + PHONE}`, result);
    lockNextSections();
  };

  const handleZipCodeFocus = (e) => {};

  const REGISTERS = {
    FIRST_NAME_REGISTER: {
      register,
      type: FIRST_NAME,
      name: formKeyMap[FIRST_NAME],
      options: {
        onChange: (e) => {
          handleInputOnChange(FIRST_NAME, processNameField(e.target.value));
        },
        onBlur: (e) => {
          handleInputOnChange(FIRST_NAME, cleanNameField(e.target.value));
          updateRedux();
          onInputBlur();
        },
      },
    },
    LAST_NAME_REGISTER: {
      register,
      type: LAST_NAME,
      name: formKeyMap[LAST_NAME],
      options: {
        onChange: (e) => {
          handleInputOnChange(LAST_NAME, processNameField(e.target.value));
        },
        onBlur: (e) => {
          handleInputOnChange(LAST_NAME, cleanNameField(e.target.value));
          updateRedux();
          onInputBlur();
        },
      },
    },
    ADDRESS1_REGISTER: {
      register,
      type: ADDRESS1,
      name: formKeyMap[ADDRESS1],
      options: {
        onChange: (e) => {
          handleInputOnChange(ADDRESS1, addressFormater(e.target.value));
        },
        onBlur: (e) => {
          if (!errors[formKeyMap[ADDRESS1]]) {
            updateRedux();
          }
          onInputBlur();
        },
      },
    },
    ADDRESS2_REGISTER: {
      register,
      type: ADDRESS2,
      name: formKeyMap[ADDRESS2],
      options: {
        onChange: (e) => {
          handleInputOnChange(ADDRESS2, addressFormater(e.target.value));
        },
        onBlur: (e) => {
          if (!errors[formKeyMap[ADDRESS2]]) {
            updateRedux();
          }
          onInputBlur();
        },
      },
    },
    CITY_REGISTER: {
      register,
      type: CITY,
      name: formKeyMap[CITY],
      options: {
        onChange: (e) => {
          handleInputOnChange(CITY, processCityField(e.target.value));
        },
        onBlur: (e) => {
          let city = e.target.value.trim();
          handleInputOnChange(CITY, city);
          updateRedux();
          onInputBlur();
        },
      },
    },
    STATE_REGISTER: {
      register,
      type: STATE,
      name: formKeyMap[STATE],
      options: {
        onChange: (e) => {
          handleInputOnChange(STATE, parseInt(e.target.value));
          updateReduxOnChange({ [STATE]: parseInt(e.target.value) });
          lockNextSections();
        },
        onBlur: (e) => {
          if (!errors[formKeyMap[STATE]]) {
          }
          onInputBlur();
        },
      },
    },
    ZIP_REGISTER: {
      register,
      type: ZIP,
      name: formKeyMap[ZIP],
      options: {
        pattern: {
          value: ZIP_CODE_REGEX[formData[COUNTRY]],
        },
        onChange: (e) => {
          clearErrors(ZIP);
          const code = validateNewZipCode(
            e.target.value.toUpperCase(),
            formData[ZIP],
            6
          );
          handleInputOnChange(ZIP, code);
        },
        onBlur: (e) => {
          updateRedux();
          const code = e.target.value.toUpperCase();
          let fomattedCode = code;
          if (code.trim().length == 6) {
            fomattedCode = `${code.slice(0, 3)} ${code.slice(3, 6)}`;
          }
          if (!errors[formKeyMap[ZIP]]) {
            handleInputOnChange(ZIP, fomattedCode);
            updateReduxOnChange({ [ZIP]: fomattedCode });
          }
          onInputBlur();
        },
      },
    },
    PHONE_REGISTER: {
      register,
      type: PHONE,
      name: formKeyMap[PHONE],
      options: {
        onChange: (e) => {
          clearErrors(PHONE);
          let number = phoneNumberLengthValidation(e.target.value);
          if (number !== null) {
            if (
              number.length >= 11 &&
              (e.target.defaultValue == '' || e.target.defaultValue.length > 11)
            ) {
              number = normalizeInput(number);
            }
            handleInputOnChange(PHONE, number);
          }
        },
        onBlur: (e) => {
          const number = normalizeInput(e.target.value);
          handleInputOnChange(PHONE, number);
          updateReduxOnChange({ [PHONE]: number });
          onInputBlur();
        },
      },
    },
    COUNTRY_REGISTER: {
      register,
      type: COUNTRY,
      name: formKeyMap[COUNTRY],
      options: {
        onChange: (e) => {
          handleInputOnChange(COUNTRY, e.target.value);
          updateReduxOnChange({ [COUNTRY]: e.target.value });
          lockNextSections();
        },
        onBlur: (e) => {
          if (!errors[formKeyMap[COUNTRY]]) {
            // updateRedux();
          }
          onInputBlur();
        },
      },
    },
  };

  useEffect(() => {
    if (!isEmptyObject(data)) {
      setFormData((old) => ({ ...old, ...data }));
      Object.entries(data)?.map(([key, value]) => {
        setValue(`${formPrefix + key}`, value, {
          shouldValidate: false,
          shouldTouch: true,
          shouldDirty: true,
        });
      });
      clearErrors();
    }
  }, [data]);

  const handleGoogleAddressChange = (address, key = ADDRESS1) => {
    handleInputOnChange(key, address);
  };

  const handleGoogleAddressSelect = (address, key = ADDRESS1) => {
    handleInputOnChange(key, address);
  };

  const handleSuggestionClick = (suggestion) => {
    if (typeof suggestion !== 'undefined') {
      const service = new google.maps.places.PlacesService(
        document.createElement('div')
      );
      service.getDetails(
        googleMapsRequestOptions(suggestion.placeId),
        (place, status) => {
          let result = googleMapsValueSplit(place);
          let selectedState = appState[result.country].filter(
            (item) => item.state_code == result?.state
          );

          handleInputOnChange(ADDRESS1, result.address1);
          handleInputOnChange(CITY, processCityField(result.city));
          handleInputOnChange(STATE, parseInt(selectedState[0].state_id));
          handleInputOnChange(ADDRESS2, '');
          handleInputOnChange(ZIP, validateNewZipCode(result.postcode));
          handleInputOnChange(COUNTRY, result.country);
        }
      );
    }
  };

  let statesList = [];
  try {
    if (
      typeof formData?.countryId != 'undefined' &&
      formData?.countryId != ''
    ) {
      statesList = [...appState[formData?.countryId]];
    }
  } catch (err) {
    console.log(err.message);
  }

  statesList.unshift({
    name: 'Select',
    state_id: '',
  });

  return (
    <div className={styles.wrapper} id={id}>
      <div className={styles.container}>
        <div className={styles.nameInfo}>
          <div className={styles.nameContainer}>
            <TextInput
              id={formKeyMap[FIRST_NAME]}
              label="First Name"
              required={true}
              isDarkBorder={isSupportPage}
              register={registerInput(REGISTERS.FIRST_NAME_REGISTER)}
              value={formData[FIRST_NAME]}
              maxLength={validationTypes[FIRST_NAME].maxLength}
              onFocus={lockNextSections}
              errors={errors}
              errorType={FIRST_NAME}
              placeholder="First Name"
            />
          </div>
          <div className={styles.nameContainer}>
            <TextInput
              id={formKeyMap[LAST_NAME]}
              label="Last Name"
              required={true}
              isDarkBorder={isSupportPage}
              register={registerInput(REGISTERS.LAST_NAME_REGISTER)}
              value={formData[LAST_NAME]}
              maxLength={validationTypes[LAST_NAME].maxLength}
              onFocus={lockNextSections}
              errors={errors}
              errorType={LAST_NAME}
              placeholder="Last Name"
            />
          </div>
        </div>
        <div className={styles.streetAddressInput}>
          {isGoogleLoaded ? (
            <PlacesAutocomplete
              key={ADDRESS1}
              value={formData[ADDRESS1]}
              searchOptions={
                addressType == 'shipping'
                  ? googleMapsSearchOptionsShipping
                  : googleMapsSearchOptionsAll
              }
              onChange={(address) => {
                handleGoogleAddressChange(addressFormater(address), ADDRESS1);
              }}
              onSelect={(address) => {
                handleGoogleAddressSelect(address, ADDRESS1);
              }}
              handleSuggestionMouseEnter={(address) => {
                handleGoogleAddressSelect(address, ADDRESS1);
              }}
              onBlur={(e) => {
                onInputBlur();
              }}
            >
              {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
              }) => {
                return (
                  <div
                    onKeyDown={(e) => {
                      if (e.key == 'Enter' || e.key == 'Tab') {
                        let selSuggestion = suggestions.filter(
                          (s) => s?.description == e.target.value
                        );
                        handleSuggestionClick(selSuggestion[0]);
                      }
                    }}
                  >
                    <TextInput
                      id={formKeyMap[ADDRESS1]}
                      label="Street Address"
                      isDarkBorder={isSupportPage}
                      required={true}
                      register={registerInput(REGISTERS.ADDRESS1_REGISTER)}
                      value={formData[ADDRESS1]}
                      errors={errors}
                      errorType={ADDRESS1}
                      placeholder="Street Address"
                      onFocus={() => {
                        setHandleSuggestion(true);
                      }}
                      {...getInputProps({
                        onBlur: (e) => {
                          trigger([ADDRESS1]);
                          onInputBlur();
                          setHandleSuggestion(false);
                          updateRedux();
                        },
                      })}
                    />
                    {handleSuggestion && (
                      <div className={styles.autocompleteDropdownContainer}>
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion, i) => {
                          const split = suggestion?.description.split(',');
                          const filtered = `${split[0]},${
                            split[1]
                          },${getValidIndexValue(split)}`;
                          const className = suggestion.active
                            ? 'suggestion-item--active'
                            : 'suggestion-item';

                          const style = suggestion.active
                            ? {
                                backgroundColor: '#fafafa',
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: '8px 10px',
                                borderBottom: '1px solid #ccc',
                                borderRadius: i === 4 ? '0 0 5px 5px' : '',
                              }
                            : {
                                backgroundColor: '',
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: '8px 10px',
                                borderBottom: '1px solid #ccc',
                                borderRadius: i === 4 ? '0 0 5px 5px' : '',
                              };

                          return (
                            <div
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <div
                                key={suggestion.terms[0].value}
                                {...getSuggestionItemProps(suggestion, {
                                  className,
                                  style,
                                })}
                              >
                                <span>{filtered}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }}
            </PlacesAutocomplete>
          ) : (
            <TextInput
              id={formKeyMap[ADDRESS1]}
              label="Street Address"
              isDarkBorder={isSupportPage}
              required={true}
              register={registerInput(REGISTERS.ADDRESS1_REGISTER)}
              value={formData[ADDRESS1]}
              onFocus={lockNextSections}
              errors={errors}
              errorType={ADDRESS1}
              placeholder="Street Address"
            />
          )}
        </div>
        <TextInput
          id={formKeyMap[ADDRESS2]}
          label="Apartment or Suite Number"
          isDarkBorder={isSupportPage}
          required={false}
          register={registerInput(REGISTERS.ADDRESS2_REGISTER)}
          value={formData[ADDRESS2]}
          onFocus={lockNextSections}
          errors={errors}
          errorType={ADDRESS2}
          placeholder="Apartment or Suite Number"
        />
        <TextInput
          id={formKeyMap[CITY]}
          label="City"
          required={true}
          isDarkBorder={isSupportPage}
          register={registerInput(REGISTERS.CITY_REGISTER)}
          value={formData[CITY]}
          maxLength={validationTypes[CITY].maxLength}
          onFocus={lockNextSections}
          errors={errors}
          errorType={CITY}
          placeholder="City"
        />
        <div className={styles.nameInfo}>
          <div className={styles.nameContainer}>
            <CountryDropdown
              id={formKeyMap[COUNTRY]}
              label="Country"
              isDarkBorder={isSupportPage}
              name={formKeyMap[COUNTRY]}
              tkey="name"
              options={countryList}
              {...registerInput(REGISTERS.COUNTRY_REGISTER)}
              // selectedOption={formData.state}
              value={formData[COUNTRY]}
              optionsOrder={[1, 2]}
              errorType={COUNTRY}
              error={errors[formKeyMap[COUNTRY]]?.type ? true : false}
              readOnly={addressType === 'shipping'}
              required
            />
            {getValidationMessage(errors, formKeyMap[COUNTRY], COUNTRY)}
          </div>
          <div className={styles.nameContainer}>
            <CountryDropdown
              id={formKeyMap[STATE]}
              label="State/Province"
              isDarkBorder={isSupportPage}
              name={formKeyMap[STATE]}
              tkey="name"
              pkey="state_id"
              options={statesList}
              {...registerInput(REGISTERS.STATE_REGISTER)}
              value={formData[STATE]}
              optionsOrder={[1, 2]}
              errorType={STATE}
              error={errors[formKeyMap[STATE]]?.type ? true : false}
              required
            />
            {getValidationMessage(errors, formKeyMap[STATE], STATE)}
          </div>
        </div>
        <div className={styles.nameInfo}>
          <div className={styles.nameContainer}>
            <TextInput
              id={formKeyMap[ZIP]}
              label="ZIP Code"
              isDarkBorder={isSupportPage}
              required={true}
              register={registerInput(REGISTERS.ZIP_REGISTER)}
              value={validateNewZipCode(formData[ZIP])}
              maxLength={validationTypes[ZIP].maxLength}
              onFocus={lockNextSections}
              errors={errors}
              errorType={ZIP}
              placeholder="ZIP Code"
            />
          </div>
          <div className={styles.nameContainer}>
            <TextInput
              type="tel"
              inputMode="numeric"
              id={formKeyMap[PHONE]}
              label="Phone Number"
              isDarkBorder={isSupportPage}
              required={true}
              register={registerInput(REGISTERS.PHONE_REGISTER)}
              onFocus={(e) => handlePhonenumberFocus(e, PHONE)}
              onClickCapture={(e) => handlePhonenumberFocus(e, PHONE)}
              value={formData[PHONE]}
              maxLength={validationTypes[PHONE].maxLength}
              errors={errors}
              errorType={PHONE}
              placeholder="(XXX) XXX-XXXX"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseAddress;
